import React, { useEffect, useState } from "react";
import { LayoutGrid, Palette, CalendarRange, ClipboardList, Tag, Plus, Trash2, ExternalLink, Upload, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveUrl, setActingTenant } from "@/lib/apiClient";
import AdminShell from "@/components/AdminShell";
import ImageCropper from "@/components/ImageCropper";
import MonthGrid, { monthOf } from "@/components/MonthGrid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "services", label: "Services", icon: Tag },
  { key: "schedule", label: "Schedule", icon: CalendarRange },
  { key: "bookings", label: "Bookings", icon: ClipboardList },
];
const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];

function ImageUpload({ value, onChange, testid, label = "Upload", rounded = "rounded-xl" }) {
  const [busy, setBusy] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const inputRef = React.useRef(null);

  const doUpload = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "crop.jpg");
      const { data } = await api.post("/my/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiErr(err, "Upload failed")); }
    finally { setBusy(false); setCropFile(null); }
  };
  return (
    <div className="flex items-center gap-3 mt-1.5">
      {value ? (
        <img src={resolveUrl(value)} alt="preview" className={`h-14 w-14 ${rounded} object-cover border border-[#E8E6E1] bg-white`} />
      ) : (
        <div className={`h-14 w-14 ${rounded} border border-dashed border-[#E8E6E1] flex items-center justify-center text-[#787672]`}><Upload className="w-4 h-4" /></div>
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); e.target.value = ""; }} className="hidden" data-testid={`upload-${testid}-input`} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`upload-${testid}-btn`} className="rounded-full">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> {value ? "Replace" : label}</>}
      </Button>
      {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} className="rounded-full text-[#993333]">Remove</Button>}
      <ImageCropper file={cropFile} open={!!cropFile} onCancel={() => setCropFile(null)} onCropped={doUpload} title="Crop logo" />
    </div>
  );
}

function GalleryUpload({ items, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const inputRef = React.useRef(null);
  const doUpload = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "photo.jpg");
      const { data } = await api.post("/my/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...(items || []), data.url]);
      toast.success("Photo added");
    } catch (err) { toast.error(apiErr(err, "Upload failed")); }
    finally { setBusy(false); setCropFile(null); }
  };
  return (
    <div className="mt-1.5">
      <div className="grid grid-cols-4 gap-2 mb-3">
        {(items || []).map((u, i) => (
          <div key={i} className="relative group aspect-square" data-testid={`gallery-item-${i}`}>
            <img src={resolveUrl(u)} alt="" className="w-full h-full object-cover rounded-lg border border-[#E8E6E1]" />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
              data-testid={`gallery-remove-${i}`}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#993333] text-white flex items-center justify-center text-xs">×</button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-add`}
          className="aspect-square rounded-lg border border-dashed border-[#E8E6E1] flex items-center justify-center text-[#787672] hover:border-[#9A8C73]">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5" />}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); e.target.value = ""; }} className="hidden" data-testid={`${testid}-input`} />
      <ImageCropper file={cropFile} open={!!cropFile} onCancel={() => setCropFile(null)} onCropped={doUpload} title="Crop photo (square)" />
    </div>
  );
}

export default function SalonAdmin({ actingTenant = null, onExit = null }) {
  const isOwn = !actingTenant;
  const [memberships, setMemberships] = useState([]);
  const [activeTid, setActiveTid] = useState(null);
  useEffect(() => {
    if (!isOwn) return;
    api.get("/my/memberships").then((r) => {
      const ms = r.data.memberships || [];
      setMemberships(ms);
      setActiveTid((cur) => cur || (ms[0]?.tenant_id ?? null));
    }).catch(() => {});
  }, [isOwn]);

  const effectiveTid = actingTenant ? actingTenant.id : activeTid;
  setActingTenant(effectiveTid);

  const [tab, setTab] = useState("overview");
  const [tenant, setTenant] = useState(null);
  useEffect(() => { api.get("/my/tenant").then((r) => setTenant(r.data)).catch(() => {}); }, [tab, actingTenant, effectiveTid]);
  const activeMembership = memberships.find((m) => m.tenant_id === effectiveTid);
  const multi = isOwn && memberships.length > 1;
  return (
    <>
      {actingTenant && (
        <div className="bg-[#2C3531] text-white px-6 py-2.5 flex items-center justify-between text-sm gap-3" data-testid="manage-banner">
          <span className="truncate">Managing <strong>{tenant?.name || actingTenant.name}</strong> as platform admin</span>
          <Button data-testid="exit-manage-btn" onClick={onExit} size="sm" variant="outline" className="rounded-full h-8 shrink-0 bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white">Exit to platform</Button>
        </div>
      )}
      {multi && (
        <div className="bg-[#F7F5F1] border-b border-[#E8E6E1] px-6 py-2.5 flex items-center gap-3 text-sm" data-testid="business-switcher-bar">
          <span className="text-[#787672] font-medium shrink-0">Managing bookings for</span>
          <Select value={effectiveTid || ""} onValueChange={setActiveTid}>
            <SelectTrigger className="w-auto min-w-[220px] h-9 rounded-full bg-white" data-testid="business-switcher"><SelectValue /></SelectTrigger>
            <SelectContent>
              {memberships.map((m) => (
                <SelectItem key={m.tenant_id} value={m.tenant_id} data-testid={`switch-to-${m.business_id}`}>{m.business_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-[#9A8C73] hidden sm:inline">Your salon hosts {memberships.length} businesses — services & schedule are set per business; the room calendar is shared.</span>
        </div>
      )}
      <AdminShell title={tenant?.name || "Salon"} subtitle={tenant ? `${activeMembership ? activeMembership.business_name + " · " : ""}Public page: /book/${tenant.slug}` : ""} tabs={TABS} active={tab} onTab={setTab}>
        <div key={effectiveTid || "none"}>
          {tab === "overview" && <Overview tenant={tenant} actingTenant={actingTenant} onTenantChange={setTenant} onExit={onExit} />}
          {tab === "branding" && <Branding tenant={tenant} onSaved={setTenant} />}
          {tab === "services" && <Services />}
          {tab === "schedule" && <Schedule />}
          {tab === "bookings" && <Bookings />}
        </div>
      </AdminShell>
    </>
  );
}

const Stat = ({ label, value }) => (
  <Card className="p-5 border-[#E8E6E1] shadow-none">
    <p className="uppercase tracking-[0.15em] text-[10px] font-bold text-[#787672]">{label}</p>
    <p className="font-serif text-3xl font-bold mt-2">{value}</p>
  </Card>
);

function PlatformControls({ tenant, onTenantChange, onExit }) {
  const [deposit, setDeposit] = useState(String(tenant.deposit_amount ?? ""));
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  useEffect(() => { setDeposit(String(tenant.deposit_amount ?? "")); }, [tenant.deposit_amount]);
  const suspended = tenant.status !== "active";

  const saveDeposit = async () => {
    const val = parseFloat(deposit);
    if (isNaN(val) || val < 0) return toast.error("Enter a valid deposit amount");
    setSaving(true);
    try {
      const { data } = await api.put(`/tenants/${tenant.id}`, { deposit_amount: val });
      onTenantChange?.(data); toast.success("Deposit updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const toggleStatus = async () => {
    try {
      const { data } = await api.put(`/tenants/${tenant.id}`, { status: suspended ? "active" : "suspended" });
      onTenantChange?.(data); toast.success(suspended ? "Salon reactivated" : "Salon suspended");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/tenants/${tenant.id}`);
      toast.success("Salon deleted"); setConfirmDel(false); onExit?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="p-6 border-[#9A8C73]/40 shadow-none bg-[#F7F5F1]" data-testid="platform-controls">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-[#9A8C73]" />
        <h3 className="font-serif text-lg font-bold">Platform controls</h3>
      </div>
      <p className="text-xs text-[#787672] mb-4">These settings are only available to the platform admin, not the salon.</p>
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <Label>Deposit amount (£)</Label>
          <Input data-testid="platform-deposit-input" type="number" min="0" step="0.01" value={deposit}
            onChange={(e) => setDeposit(e.target.value)} placeholder="25" className="max-w-[220px]" />
        </div>
        <Button data-testid="platform-save-deposit" onClick={saveDeposit} disabled={saving}
          className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">{saving ? "Saving…" : "Save deposit"}</Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-[#E8E6E1]">
        <Button data-testid="platform-toggle-status" onClick={toggleStatus} variant="outline" className="rounded-full">
          {suspended ? "Reactivate salon" : "Suspend salon"}
        </Button>
        <Button data-testid="platform-delete-salon" onClick={() => setConfirmDel(true)} variant="outline"
          className="rounded-full text-[#993333] border-[#993333]/40 hover:bg-[#993333]/10">
          <Trash2 className="w-4 h-4 mr-1.5" /> Delete salon
        </Button>
        <Badge variant={suspended ? "secondary" : "default"} className="self-center ml-auto">{tenant.status}</Badge>
      </div>
      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent data-testid="delete-salon-dialog">
          <DialogHeader><DialogTitle className="font-serif">Delete {tenant.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#787672]">This permanently removes the salon, its admin login, schedule and all bookings. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="confirm-delete-salon" onClick={doDelete} className="rounded-full bg-[#993333] hover:bg-[#7a2828]">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function Overview({ tenant, actingTenant = null, onTenantChange = null, onExit = null }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics/salon").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <p className="text-[#787672]">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="salon-overview">
      {actingTenant && tenant && (
        <PlatformControls tenant={tenant} onTenantChange={onTenantChange} onExit={onExit} />
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Bookings" value={a.total_bookings} />
        <Stat label="Deposits Collected" value={`£${a.deposits_collected}`} />
        <Stat label="Balance Collected" value={`£${a.balance_collected}`} />
        <Stat label="Upcoming" value={a.upcoming} />
      </div>
      {tenant && (
        <Card className="p-6 border-[#E8E6E1] shadow-none flex items-center justify-between">
          <div><h3 className="font-serif text-lg font-bold">Your booking page is live</h3>
            <p className="text-sm text-[#787672]">/book/{tenant.slug}</p></div>
          <a href={`/book/${tenant.slug}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full">Open <ExternalLink className="w-4 h-4 ml-1" /></Button>
          </a>
        </Card>
      )}
    </div>
  );
}

function Branding({ tenant, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (tenant) setF(tenant); }, [tenant]);
  if (!f) return <p className="text-[#787672]">Loading…</p>;
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    try {
      const { data } = await api.put("/my/tenant/branding", {
        name: f.name, logo_url: f.logo_url, logo_width: f.logo_width ?? "", logo_height: f.logo_height ?? "",
        description: f.description, gallery: f.gallery || [],
        primary_color: f.primary_color,
        secondary_color: f.secondary_color, address: f.address, phone: f.phone, email: f.email,
        prep_instructions: f.prep_instructions,
      });
      onSaved(data); toast.success("Branding saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-5xl" data-testid="branding-panel">
      <div className="space-y-4">
        <Card className="p-5 border-[#E8E6E1] shadow-none space-y-3">
          <h3 className="font-serif text-lg font-bold">Identity</h3>
          <div><Label>Salon name</Label><Input value={f.name || ""} onChange={(e) => upd("name", e.target.value)} /></div>
          <div><Label>Logo</Label><ImageUpload value={f.logo_url} onChange={(u) => upd("logo_url", u)} testid="logo" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Logo width (px)</Label><Input type="number" data-testid="brand-logo-width" value={f.logo_width ?? ""} onChange={(e) => upd("logo_width", e.target.value === "" ? "" : Number(e.target.value))} placeholder="auto" /></div>
            <div><Label>Logo height (px)</Label><Input type="number" data-testid="brand-logo-height" value={f.logo_height ?? ""} onChange={(e) => upd("logo_height", e.target.value === "" ? "" : Number(e.target.value))} placeholder="44" /></div>
          </div>
          <p className="text-xs text-[#9A8C73]">Controls how large your logo appears in your booking page header. Leave width blank to keep the natural ratio.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Primary color</Label><div className="flex gap-2"><input type="color" value={f.primary_color || "#9A8C73"} onChange={(e) => upd("primary_color", e.target.value)} className="h-10 w-12 rounded" data-testid="brand-primary-color" /><Input value={f.primary_color || ""} onChange={(e) => upd("primary_color", e.target.value)} /></div></div>
            <div><Label>Secondary color</Label><div className="flex gap-2"><input type="color" value={f.secondary_color || "#2C3531"} onChange={(e) => upd("secondary_color", e.target.value)} className="h-10 w-12 rounded" /><Input value={f.secondary_color || ""} onChange={(e) => upd("secondary_color", e.target.value)} /></div></div>
          </div>
        </Card>
        <Card className="p-5 border-[#E8E6E1] shadow-none space-y-3">
          <h3 className="font-serif text-lg font-bold">About your salon</h3>
          <div><Label>Description</Label><Textarea data-testid="brand-description" rows={4} value={f.description || ""} onChange={(e) => upd("description", e.target.value)} placeholder="Tell customers about your salon…" /></div>
          <div><Label>Photo gallery (square)</Label><GalleryUpload items={f.gallery} onChange={(g) => upd("gallery", g)} testid="gallery" /></div>
        </Card>
        <Card className="p-5 border-[#E8E6E1] shadow-none space-y-3">
          <h3 className="font-serif text-lg font-bold">Contact</h3>
          <div><Label>Phone</Label><Input value={f.phone || ""} onChange={(e) => upd("phone", e.target.value)} /></div>
          <div><Label>Address</Label><Input value={f.address || ""} onChange={(e) => upd("address", e.target.value)} /></div>
          <p className="text-xs text-[#787672]">Your service prices are managed in the <span className="font-semibold">Services</span> tab. Deposits are set per service by your provider.</p>
          <div><Label>Prep instructions</Label><Textarea value={f.prep_instructions || ""} onChange={(e) => upd("prep_instructions", e.target.value)} /></div>
        </Card>
        <Button data-testid="save-branding-btn" onClick={save} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] w-full h-11">Save branding</Button>
      </div>
      <div>
        <p className="uppercase tracking-[0.15em] text-[10px] font-bold text-[#787672] mb-3">Live preview</p>
        <div className="rounded-2xl overflow-hidden border border-[#E8E6E1] bg-white sticky top-28">
          <div className="p-5 flex items-center gap-3 border-b border-[#F0EFEB]">
            {f.logo_url
              ? <img src={resolveUrl(f.logo_url)} alt="logo" className="h-10 w-10 rounded-lg object-cover" />
              : <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-serif font-bold" style={{ background: f.primary_color }}>{(f.name || "S")[0]}</div>}
            <span className="font-serif font-bold" style={{ color: f.secondary_color }}>{f.name}</span>
          </div>
          <div className="p-5">
            <h1 className="font-serif text-2xl font-bold mb-2" style={{ color: f.secondary_color }}>{f.name}</h1>
            <p className="text-sm text-[#787672] leading-relaxed line-clamp-4">{f.description || "Your salon description appears here."}</p>
            {(f.gallery || []).length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {f.gallery.slice(0, 3).map((u, i) => <img key={i} src={resolveUrl(u)} alt="" className="aspect-square object-cover rounded-lg" />)}
              </div>
            )}
            <div className="mt-4 rounded-xl p-4 text-white" style={{ background: f.primary_color }}>
              <p className="text-sm">Services & prices are shown on your live booking page.</p>
            </div>
            <button className="mt-3 w-full py-3 rounded-full text-white font-semibold" style={{ background: f.secondary_color }}>Book now</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Services() {
  const [services, setServices] = useState([]);
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/my/business/services").then((r) => {
    setServices(r.data.services || []);
    const m = {};
    (r.data.selection || []).forEach((s) => { m[s.service_id] = { offered: true, markup: s.markup ?? 0 }; });
    setSel(m);
  });
  useEffect(() => { load(); }, []);
  const toggle = (id, on) => setSel((p) => ({ ...p, [id]: { offered: on, markup: p[id]?.markup ?? 0 } }));
  const setMarkup = (id, v) => setSel((p) => ({ ...p, [id]: { offered: p[id]?.offered ?? true, markup: v } }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(sel).filter(([, v]) => v.offered).map(([service_id, v]) => ({ service_id, markup: Number(v.markup) || 0 }));
      await api.put("/my/services", { services: payload });
      toast.success("Services updated"); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="salon-services">
      <div className="flex items-start justify-between gap-4">
        <div><h3 className="font-serif text-xl font-bold">Your services</h3>
          <p className="text-sm text-[#787672]">Choose which services to offer and add your markup. Customers pay the base price plus your markup.</p></div>
        <Button data-testid="save-services-btn" onClick={save} disabled={saving} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] shrink-0">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save services"}</Button>
      </div>
      {services.length === 0 && <p className="text-[#787672]">Your provider hasn't published any services yet.</p>}
      {services.map((s) => {
        const st = sel[s.id] || { offered: false, markup: 0 };
        const total = (Number(s.base_price) || 0) + (Number(st.markup) || 0);
        return (
          <Card key={s.id} data-testid={`service-${s.id}`} className={`p-5 border-[#E8E6E1] shadow-none ${st.offered ? "" : "opacity-60"}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <Switch data-testid={`service-toggle-${s.id}`} checked={st.offered} onCheckedChange={(v) => toggle(s.id, v)} />
                  <h4 className="font-serif text-lg font-bold">{s.name}</h4>
                </div>
                {s.description && <p className="text-sm text-[#787672] mt-1 ml-[52px]">{s.description}</p>}
                <p className="text-xs text-[#9A8C73] mt-1 ml-[52px]">Base £{s.base_price} · Deposit £{s.deposit_amount}{s.duration_min ? ` · ${s.duration_min} min` : ""}</p>
              </div>
              <div className="w-40 shrink-0">
                <Label className="text-xs">Your markup (£)</Label>
                <Input type="number" min="0" data-testid={`service-markup-${s.id}`} value={st.markup} disabled={!st.offered}
                  onChange={(e) => setMarkup(s.id, e.target.value === "" ? "" : Number(e.target.value))} />
                <p className="text-xs text-[#787672] mt-1">Client pays <span className="font-bold text-[#2C3531]">£{total}</span></p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function Schedule() {
  const [combined, setCombined] = useState({});
  const [sched, setSched] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [capDefault, setCapDefault] = useState(1);
  const [overrides, setOverrides] = useState({});
  const [savingCap, setSavingCap] = useState(false);
  useEffect(() => {
    api.get("/my/combined-availability").then((r) => setCombined(r.data || {}));
    api.get("/my/schedule").then((r) => { setSched(r.data.schedules || {}); setLoaded(true); });
    api.get("/my/location").then((r) => { setCapDefault(r.data.capacity_default ?? 1); setOverrides(r.data.timeslot_capacity || {}); }).catch(() => {});
  }, []);
  const toggleSlot = (day, t) => {
    const cur = sched[day] || [];
    const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t].sort();
    setSched({ ...sched, [day]: next });
  };
  const dayOn = (day) => (sched[day] || []).length > 0;
  const toggleDay = (day) => setSched({ ...sched, [day]: dayOn(day) ? [] : (combined[day] || []) });
  const save = async () => {
    const full = DAYS.reduce((acc, [d]) => ({ ...acc, [d]: sched[d] || [] }), {});
    try { await api.put("/my/schedule", { schedules: full }); toast.success("Schedule saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const allTimes = Array.from(new Set(Object.values(combined).flat())).sort();
  const setOverride = (t, v) => setOverrides((p) => { const n = { ...p }; if (v === "" || Number(v) === Number(capDefault)) delete n[t]; else n[t] = Number(v); return n; });
  const saveCapacity = async () => {
    setSavingCap(true);
    try { await api.put("/my/location/capacity", { capacity_default: Number(capDefault) || 1, timeslot_capacity: overrides }); toast.success("Room capacity saved"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingCap(false); }
  };
  return (
    <div data-testid="schedule-panel" className="max-w-3xl">
      <Card className="p-5 border-[#9A8C73]/40 shadow-none bg-[#F7F5F1] mb-6" data-testid="capacity-panel">
        <h3 className="font-serif text-lg font-bold">Shared room capacity</h3>
        <p className="text-sm text-[#787672] mt-1 mb-3">How many appointments your salon can run <strong>at the same time</strong>, across <em>all</em> the businesses you host. A booking under any business blocks the room for its full length.</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label>Default concurrent appointments</Label>
            <Input type="number" min="1" data-testid="capacity-default" value={capDefault}
              onChange={(e) => setCapDefault(e.target.value === "" ? "" : Number(e.target.value))} className="w-40" />
          </div>
          <Button data-testid="save-capacity-btn" onClick={saveCapacity} disabled={savingCap} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">{savingCap ? "Saving…" : "Save capacity"}</Button>
        </div>
        {allTimes.length > 0 && (
          <details className="mt-4">
            <summary className="text-sm font-semibold cursor-pointer text-[#2C3531]" data-testid="capacity-overrides-toggle">Per-time overrides (optional)</summary>
            <p className="text-xs text-[#787672] mt-2 mb-2">Leave blank to use the default ({capDefault || 1}). Set a number to allow more (or fewer) at a specific time.</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {allTimes.map((t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <span className="text-xs text-[#787672] w-10 shrink-0">{t}</span>
                  <Input type="number" min="1" data-testid={`capacity-override-${t}`} value={overrides[t] ?? ""} placeholder={String(capDefault || 1)}
                    onChange={(e) => setOverride(t, e.target.value)} className="h-8" />
                </div>
              ))}
            </div>
          </details>
        )}
      </Card>
      <p className="text-sm text-[#787672] mb-4">Pick the times you want to offer on your booking page for each day. Times shown are the combined availability of the platform's travelling technicians.</p>
      <div className="space-y-3">
        {DAYS.map(([key, label]) => {
          const times = combined[key] || [];
          return (
            <Card key={key} className="p-4 border-[#E8E6E1] shadow-none">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">{label}</span>
                <Switch checked={dayOn(key)} disabled={times.length === 0} onCheckedChange={() => toggleDay(key)} data-testid={`day-toggle-${key}`} />
              </div>
              {times.length === 0 ? (
                <p className="text-xs text-[#B0AEA8] italic">No technician availability on this day.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {times.map((t) => {
                    const on = (sched[key] || []).includes(t);
                    return (
                      <button key={t} onClick={() => toggleSlot(key, t)} data-testid={`slot-chip-${key}-${t}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${on ? "bg-[#2C3531] text-white" : "bg-[#F0EFEB] text-[#787672] hover:bg-[#E8E6E1]"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>
      <Button data-testid="save-schedule-btn" disabled={!loaded} onClick={save} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] mt-5 h-11">Save schedule</Button>
    </div>
  );
}

function Bookings() {
  const [list, setList] = useState([]);
  const [slots, setSlots] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("list");
  const [monthOffset, setMonthOffset] = useState(0);
  const [dragId, setDragId] = useState(null);
  const [rebook, setRebook] = useState(null);
  const [rf, setRf] = useState({ date: "", slot_id: "" });
  const load = () => api.get("/my/bookings").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/my/combined-availability").then((r) => {
      const all = Array.from(new Set(Object.values(r.data || {}).flat())).sort();
      setSlots(all.map((t) => ({ id: t, start_time: t, end_time: "" })));
    });
  }, []);
  // auto-refresh: new customer bookings appear without reload
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const doRebook = async () => {
    try { await api.put(`/my/bookings/${rebook.id}/rebook`, rf); toast.success("Rebooked"); setRebook(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const complete = async (b) => {
    try { await api.put(`/my/bookings/${b.id}/complete`, { balance_method: "cash" }); toast.success("Marked completed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const dropRebook = async (b, dateStr, slotId) => {
    if (!b || (b.date === dateStr && b.slot_id === slotId)) return;
    try { await api.put(`/my/bookings/${b.id}/rebook`, { date: dateStr, slot_id: slotId }); toast.success(`Moved ${b.ref} to ${dateStr}`); load(); loadAvail(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const filtered = list.filter((b) => (b.customer_name + b.customer_email + b.ref).toLowerCase().includes(q.toLowerCase()));
  const statusColor = { confirmed: "default", rebooked: "default", completed: "secondary" };

  const draggedBooking = list.find((b) => b.id === dragId);
  const monthDate = monthOf(monthOffset);
  const dayBookings = (ds) => list
    .filter((b) => b.date === ds && b.status !== "cancelled")
    .sort((a, b) => (a.slot_id || "").localeCompare(b.slot_id || ""));

  const renderDay = (ds) => dayBookings(ds).map((b) => (
    <div key={b.id} draggable onDragStart={() => setDragId(b.id)} onDragEnd={() => setDragId(null)}
      data-testid={`cal-booking-${b.ref}`}
      className="text-[10px] leading-tight rounded px-1.5 py-0.5 cursor-grab active:cursor-grabbing text-white truncate"
      style={{ background: b.status === "completed" ? "#787672" : "#2C3531" }}
      title={`${b.slot_id} · ${b.customer_name} · ${b.staff_name}`}>
      {b.slot_id} · {b.customer_name.split(" ")[0]}
    </div>
  ));

  const cellProps = (ds, inMonth) => {
    if (!inMonth || !draggedBooking) return {};
    return {
      onDragOver: (e) => e.preventDefault(),
      onDrop: () => { dropRebook(draggedBooking, ds, draggedBooking.slot_id); setDragId(null); },
      className: "bg-[#3B5341]/10",
    };
  };

  return (
    <div data-testid="bookings-panel" className="max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex rounded-full border border-[#E8E6E1] p-1 bg-white">
          <button data-testid="view-list-btn" onClick={() => setView("list")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "list" ? "bg-[#2C3531] text-white" : "text-[#787672]"}`}>List</button>
          <button data-testid="view-calendar-btn" onClick={() => setView("calendar")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "calendar" ? "bg-[#2C3531] text-white" : "text-[#787672]"}`}>Calendar</button>
        </div>
        {view === "list" && <Input data-testid="booking-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bookings…" className="max-w-xs" />}
      </div>

      {view === "calendar" && (
        <div data-testid="calendar-view">
          <p className="text-xs text-[#787672] mb-3">Drag any appointment onto another day to reschedule it (keeps the same time).</p>
          <MonthGrid monthDate={monthDate} testid="salon-bookings-month"
            onPrev={() => setMonthOffset(monthOffset - 1)} onNext={() => setMonthOffset(monthOffset + 1)} onToday={() => setMonthOffset(0)}
            renderDay={renderDay} getCellProps={cellProps} />
        </div>
      )}

      {view === "list" && (
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-[#787672]">No bookings yet.</p>}
        {filtered.map((b) => (
          <Card key={b.id} className="p-4 border-[#E8E6E1] shadow-none" data-testid={`booking-row-${b.ref}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">{b.customer_name} <span className="text-xs text-[#787672]">· {b.ref}</span></p>
                <p className="text-sm text-[#787672]">{b.date} · {b.slot_start} · {b.staff_name} · {b.customer_phone}</p>
                <p className="text-xs text-[#787672] mt-0.5">Deposit £{b.deposit_amount} paid · Balance £{b.balance_due} due in-clinic</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={statusColor[b.status] || "default"}>{b.status}</Badge>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button data-testid={`rebook-btn-${b.ref}`} onClick={() => { setRebook(b); setRf({ date: b.date, slot_id: b.slot_id }); }} size="sm" variant="outline" className="rounded-full">Rebook</Button>
              {b.status !== "completed" && <Button data-testid={`complete-btn-${b.ref}`} onClick={() => complete(b)} size="sm" variant="outline" className="rounded-full">Mark completed</Button>}
            </div>
          </Card>
        ))}
      </div>
      )}

      <Dialog open={!!rebook} onOpenChange={(o) => !o && setRebook(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif">Reschedule {rebook?.ref}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>New date</Label><Input type="date" data-testid="rebook-date" value={rf.date} onChange={(e) => setRf({ ...rf, date: e.target.value })} /></div>
            <div><Label>New slot</Label>
              <Select value={rf.slot_id} onValueChange={(v) => setRf({ ...rf, slot_id: v })}>
                <SelectTrigger data-testid="rebook-slot"><SelectValue placeholder="Choose slot" /></SelectTrigger>
                <SelectContent>{slots.map((s) => <SelectItem key={s.id} value={s.id}>{s.start_time}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="rebook-confirm" onClick={doRebook} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">Confirm move</Button></DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
